/**
 * PlayerVideoCard — a full-size camera card with configurable overlay zones.
 *
 * Used in ScoringPhase, RevealingPhase, RoundResults, GameOver, and SongSelectionPhase
 * to render a 16:9 video feed (or avatar/fallback) with semi-transparent gradient
 * overlays for phase-specific UI elements (score inputs, prediction labels, sliders, etc.)
 *
 * Overlay zones (render props):
 *   renderTopLeft    — top-left (player name, phase label)
 *   renderTopRight   — top-right (score display / locked value)
 *   renderBottom     — bottom area content (e.g. slider)
 *
 * Three-dots menu is always positioned absolutely in the top-right corner.
 *
 * Also exports:
 *   VideoElement   — attaches a MediaStream to a <video> element
 *   KickDialog     — confirmation dialog for kicking a player
 *
 * Grid layout utility is in src/utils/videoGrid.js
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useWebRTC } from '../context/WebRTCContext';
import ThemedCheckbox from './ThemedCheckbox';

// ── Kick confirmation dialog ──────────────────────────────────────────────────
export function KickDialog({ name, onConfirm, onCancel, kicking }) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('players.kickConfirmTitle')}
    >
      <div className="t-bg-surface t-border border rounded-2xl p-6 w-full max-w-sm space-y-4">
        <div className="text-center space-y-1">
          <p className="font-bold t-text">{t('players.kickConfirmTitle')}</p>
          <p className="text-sm t-text-muted">
            {t('players.kickConfirmBody', { name })}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 t-bg-input hover:opacity-80 t-text-secondary font-medium rounded-lg px-4 py-2.5 text-sm t-focus-ring"
          >
            {t('players.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={kicking}
            className="flex-1 t-btn-error disabled:opacity-50 font-semibold rounded-lg px-4 py-2.5 text-sm t-focus-ring"
          >
            {kicking ? '…' : t('players.kickConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Video element ─────────────────────────────────────────────────────────────
/**
 * Attaches a MediaStream to an absolutely-positioned <video> element that
 * fills its container. Calls play() explicitly to handle Chrome's unreliable
 * autoplay after unmount/remount, and re-calls play() when receiver tracks
 * unmute (Firefox / Chrome pre-negotiation edge case).
 */
export function VideoElement({ stream, muted, volume, onStall, visuallyHidden = false }) {
  const videoRef = useRef(null);
  const retryCountRef = useRef(0);
  const healthTimerRef = useRef(null);
  const pendingAutoplayCleanupRef = useRef(null);
  // Keep a stable ref to onStall so the setTimeout closure doesn't go stale.
  const onStallRef = useRef(onStall);
  useEffect(() => { onStallRef.current = onStall; }, [onStall]);

  function scheduleHealthCheck(el, activeStream, videoTrack) {
    const delay = retryCountRef.current === 0 ? 2500 : 1500;
    healthTimerRef.current = setTimeout(() => {
      if (!el || el.srcObject !== activeStream) return;
      if (videoTrack.readyState !== 'live' || videoTrack.muted) return;
      if (el.videoWidth > 0) return;

      const attempt = ++retryCountRef.current;
      if (attempt === 1) {
        el.srcObject = null;
        el.srcObject = activeStream;
        el.play().catch(() => {});
        scheduleHealthCheck(el, activeStream, videoTrack);
      } else if (attempt === 2) {
        onStallRef.current?.(2);
      } else if (attempt === 3) {
        onStallRef.current?.(3);
      }
    }, delay);
  }

  // ── Effect 1: glitch-free stream attachment ───────────────────────────────────
  // Guards against re-attaching srcObject when LiveKit's useMemo creates a new
  // MediaStream wrapper around the *same* underlying tracks (happens on any
  // useTracks fire — mute/unmute of any participant). Re-attaching causes a
  // momentary black frame and audio glitch.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;

    if (el.srcObject instanceof MediaStream) {
      const incomingVideo = stream.getVideoTracks()[0];
      const currentVideo = el.srcObject.getVideoTracks()[0];
      const incomingAudio = stream.getAudioTracks()[0];
      const currentAudio = el.srcObject.getAudioTracks()[0];

      const videoUnchanged = incomingVideo?.id === currentVideo?.id;
      const audioUnchanged = incomingAudio?.id === currentAudio?.id;

      if (videoUnchanged && audioUnchanged) return;

      if (!incomingVideo && currentVideo && audioUnchanged) {
        // Camera just turned off but audio track is the same — keep current srcObject
        // so audio is uninterrupted; the ended video track shows black naturally.
        return;
      }
    }

    pendingAutoplayCleanupRef.current?.();
    pendingAutoplayCleanupRef.current = null;

    el.srcObject = stream;
    el.play().catch((err) => {
      if (err.name !== 'NotAllowedError') return;
      const resume = () => {
        pendingAutoplayCleanupRef.current?.();
        pendingAutoplayCleanupRef.current = null;
        if (el.srcObject !== stream) return;
        el.play().catch(() => {});
      };
      document.addEventListener('click', resume, { once: true });
      document.addEventListener('touchstart', resume, { once: true });
      pendingAutoplayCleanupRef.current = () => {
        document.removeEventListener('click', resume);
        document.removeEventListener('touchstart', resume);
      };
    });

    return () => {
      pendingAutoplayCleanupRef.current?.();
      pendingAutoplayCleanupRef.current = null;
    };
  }, [stream]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 2: track lifecycle (unmute listeners + health check) ───────────────
  // Runs on every stream prop change — including intermediate wrapper-only updates
  // where Effect 1 returns early. This is intentional: Effect 1's cleanup removes
  // the unmute listeners added by the previous Effect 2 run, so Effect 2 must
  // re-attach them unconditionally to prevent the mic track's `unmute` event from
  // being missed during the window between stream attachment and first audio data.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return;

    function handleUnmute() { el.play().catch(() => {}); }
    const tracks = stream.getTracks();
    tracks.forEach((t) => t.addEventListener('unmute', handleUnmute));

    // Health check: detect black frames (video track live but no decoded frames).
    // Only meaningful when Effect 1 actually attached this stream; the internal
    // `el.srcObject !== activeStream` guard exits immediately otherwise.
    retryCountRef.current = 0;
    clearTimeout(healthTimerRef.current);
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      scheduleHealthCheck(el, stream, videoTrack);
    }

    return () => {
      clearTimeout(healthTimerRef.current);
      tracks.forEach((t) => t.removeEventListener('unmute', handleUnmute));
    };
  }, [stream]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.volume = Math.max(0, Math.min(1, volume ?? 1));
  }, [volume]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      className={`absolute inset-0 w-full h-full object-cover${visuallyHidden ? ' invisible' : ''}`}
      aria-hidden="true"
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────
/**
 * PlayerVideoCard renders a 16:9 aspect-ratio camera card.
 *
 * Props:
 *   uid              — player UID
 *   name             — display name
 *   avatarEmoji      — emoji shown in camera-off fallback (defaults to '🎵')
 *   isMe             — is this the viewer's own card?
 *   isViewerChatter  — is the VIEWER a chatter? (suppresses menu entirely)
 *   isChattersSlot   — is this the virtual Chatters player? (no camera ever)
 *
 *   WebRTC media:
 *   mediaState, localCameraStream, remoteStreamsByUid, cameraStreamId
 *   myCameraEnabled, myMicEnabled, toggleCamera, toggleMic
 *
 *   Remote controls (other players only):
 *   isMuted, volume, isHidden
 *   onMuteToggle, onVolumeChange, onHideToggle, onKick
 *
 *   Overlay render props:
 *   renderTopLeft    — () => ReactNode
 *   renderTopRight   — () => ReactNode
 *   renderBottom     — () => ReactNode
 *
 *   Appearance:
 *   highlight        — boolean (accent ring, used for featured cards)
 *   className        — additional classes on the outer wrapper
 */
export default function PlayerVideoCard({
  uid,
  name,
  avatarEmoji = '🎵',
  isMe = false,
  isViewerChatter = false,
  isChattersSlot = false,
  // WebRTC media
  mediaState,
  localCameraStream,
  remoteStreamsByUid,
  cameraStreamId,
  myCameraEnabled,
  myMicEnabled,
  toggleCamera,
  toggleMic,
  // Remote controls
  isMuted = false,
  volume = 1,
  isHidden = false,
  onMuteToggle,
  onVolumeChange,
  onHideToggle,
  onKick,
  // Overlay render props
  renderTopLeft,
  renderTopRight,
  renderBottom,
  // Appearance
  highlight = false,
  className = '',
  // When true, the card fills its parent container instead of enforcing 16:9
  stretch = false,
  // When true, camera/mic toggle buttons are laid out in a row instead of a column (for small cards)
  compactMediaButtons = false,
  // Called when the card background area is clicked (not on controls)
  onCardClick,
  // In in-person mode WebRTC controls are unavailable; only kick is shown (host only)
  inPersonMode = false,
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  const webrtc = useWebRTC() ?? {};
  const {
    retryStreamBinding,
    reconnectPeer,
    audioConstraints,
    changeAudioConstraints,
    krispEnabled,
    krispSupported,
    toggleKrisp,
    selectedMicId,
    selectedCameraId,
    changeMicDevice,
    changeCameraDevice,
    micEnabled: myMicActive,
    cameraEnabled: myCameraActive,
  } = webrtc;

  // Available media devices — enumerated when the own-card menu opens.
  const [availableMics, setAvailableMics] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);

  useEffect(() => {
    if (!menuOpen || !isMe) return;
    navigator.mediaDevices?.enumerateDevices().then((devices) => {
      setAvailableMics(devices.filter((d) => d.kind === 'audioinput'));
      setAvailableCameras(devices.filter((d) => d.kind === 'videoinput'));
    }).catch(() => {});
  }, [menuOpen, isMe]);

  const handleVideoStall = useCallback((tier) => {
    if (!uid || isMe) return;
    if (tier === 2) retryStreamBinding?.(uid);
    else if (tier === 3) reconnectPeer?.(uid);
  }, [uid, isMe, retryStreamBinding, reconnectPeer]);

  const cameraOn = isMe ? myCameraEnabled : (mediaState?.cameraEnabled ?? false);
  const micOn = isMe ? myMicEnabled : (mediaState?.micEnabled ?? false);

  // Resolve which stream to display / play
  // displayStream: shown visually (camera on + not hidden)
  // audioOnlyStream: rendered invisible so audio plays when camera is off or hidden
  let displayStream = null;
  let audioOnlyStream = null;
  if (isMe && localCameraStream && cameraOn) {
    displayStream = localCameraStream;
  } else if (!isMe && !isChattersSlot && remoteStreamsByUid && cameraStreamId) {
    const remoteStream = remoteStreamsByUid?.get(cameraStreamId) ?? null;
    if (remoteStream) {
      if (cameraOn && !isHidden) {
        displayStream = remoteStream;
      } else {
        // Camera off or locally hidden — still need to play audio
        audioOnlyStream = remoteStream;
      }
    }
  }

  // Close menu on outside click or scroll
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      const inButton = buttonRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inButton && !inDropdown) setMenuOpen(false);
    }
    function handleScroll() { setMenuOpen(false); }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuOpen]);

  const hasTopOverlay = renderTopLeft || renderTopRight;
  // In in-person mode: only show menu on other players' cards when the viewer is host (onKick present)
  const showMenu = !isViewerChatter && !isChattersSlot &&
    (!inPersonMode || (!isMe && !!onKick));

  // Device labels — only populated once the user has granted getUserMedia permission.
  // Gate selectors on permission being granted (mic/camera enabled at least once).
  const showMicSelector = isMe && myMicActive && availableMics.length > 1;
  const showCameraSelector = isMe && myCameraActive && availableCameras.length > 1;

  return (
    <div
      className={`relative rounded-xl bg-black ${stretch ? 'h-full' : ''} ${
        highlight
          ? 'ring-2 ring-(--c-text-accent)'
          : 't-border border'
      } ${className}`}
      onClick={onCardClick || undefined}
    >
      {/* Inner container: 16:9 by default, or fills parent when stretch=true.
          overflow-hidden is here (not on the outer div) so the three-dots menu
          dropdown can escape the card bounds without being clipped. */}
      <div className={stretch ? 'absolute inset-0 overflow-hidden rounded-xl' : 'aspect-video w-full relative overflow-hidden rounded-xl'}>

        {/* Own card: camera preview. Always muted — no self-audio feedback. */}
        {isMe && displayStream && (
          <VideoElement
            stream={displayStream}
            muted
            volume={undefined}
            onStall={undefined}
          />
        )}

        {/* Remote players: a single VideoElement is always mounted when a stream
            exists, toggling visuallyHidden when camera is off. Keeping one stable
            element prevents unmount/remount on camera toggle, which would interrupt
            audio while the browser decoder restarts. */}
        {!isMe && !isChattersSlot && (displayStream || audioOnlyStream) && (
          <VideoElement
            stream={displayStream || audioOnlyStream}
            muted={false}
            volume={isMuted ? 0 : volume}
            visuallyHidden={!displayStream}
            onStall={handleVideoStall}
          />
        )}

        {/* Avatar fallback — visible whenever there is no displayStream */}
        {!displayStream && (
          <div className="absolute inset-0 t-bg-input flex flex-col items-center justify-center gap-1 px-2">
            <span className="text-3xl sm:text-4xl select-none" aria-hidden="true">
              {isChattersSlot ? '💬' : avatarEmoji}
            </span>
            {!isChattersSlot && (
              <span className="text-xs t-text-muted truncate max-w-full text-center">
                {name}
              </span>
            )}
          </div>
        )}

        {/* cursor-text on card background when keyboard capture is active */}
        {onCardClick && (
          <div
            className="absolute inset-0 z-1 cursor-text pointer-events-none"
            aria-hidden="true"
          />
        )}

        {/* Camera + Mic icon buttons — top-left, own card only */}
        {isMe && !isChattersSlot && (toggleCamera || toggleMic) && (
          <div
            className={`absolute left-1.5 z-10 flex ${compactMediaButtons ? 'flex-row' : 'flex-col'} gap-1`}
            style={{ top: hasTopOverlay ? '2.25rem' : '0.5rem' }}
          >
            {toggleCamera && (
              <MediaToggleButton
                on={cameraOn}
                onClick={toggleCamera}
                aria-label={cameraOn ? t('players.turnOffCamera') : t('players.turnOnCamera')}
                icon={<CameraIcon />}
              />
            )}
            {toggleMic && (
              <MediaToggleButton
                on={micOn}
                onClick={toggleMic}
                aria-label={micOn ? t('players.muteMic') : t('players.unmuteMic')}
                icon={<MicIcon />}
              />
            )}
          </div>
        )}

        {/* Mic-on indicator for OTHER players (read-only, no button) */}
        {!isMe && micOn && (
          <span
            className="absolute left-2 z-10 text-xs bg-(--c-bg-page)/70 rounded px-1 py-0.5 pointer-events-none"
            style={{ top: hasTopOverlay ? '2.25rem' : '0.5rem' }}
            aria-label={t('players.micOn')}
          >
            🎤
          </span>
        )}

        {/* ── Top gradient overlay ─────────────────────────────────────────── */}
        {hasTopOverlay && (
          <div className="absolute inset-x-0 top-0 z-10 bg-linear-to-b from-(--c-bg-page)/85 via-(--c-bg-page)/50 to-transparent px-2 pt-1.5 pb-8 pointer-events-none">
            {/* pr-7 reserves space so renderTopRight doesn't overlap the three-dots button */}
            <div className="flex items-start gap-1 pointer-events-auto pr-7">
              <div className="flex-1 min-w-0">
                {renderTopLeft?.()}
              </div>
              <div className="shrink-0">
                {renderTopRight?.()}
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom gradient overlay ──────────────────────────────────────── */}
        {renderBottom && (
          <div className="absolute inset-x-0 bottom-0 z-10 bg-linear-to-t from-(--c-bg-page)/90 via-(--c-bg-page)/50 to-transparent px-2 pb-1.5 pt-8">
            {renderBottom()}
          </div>
        )}
      </div>

      {/* ── Three-dots menu — outside the overflow-hidden inner div so the
          dropdown is never clipped by the card bounds ───────────────────── */}
      {showMenu && (
        <div className="absolute top-1.5 right-1.5 z-20">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => {
              if (!menuOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
              }
              setMenuOpen((o) => !o);
            }}
            aria-label={t('players.openMenu', { name })}
            aria-expanded={menuOpen}
            className="w-6 h-6 flex items-center justify-center rounded bg-(--c-bg-page)/65 hover:bg-(--c-bg-page)/85 t-text text-sm leading-none t-focus-ring"
          >
            ⋮
          </button>

          {menuOpen && createPortal(
            <div
              ref={dropdownRef}
              style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
              className="t-bg-surface t-border border rounded-xl shadow-lg py-1 min-w-48 text-sm"
              role="menu"
            >
              {isMe ? (
                <>
                  {/* ── Camera / Mic toggles ──────────────────────────────── */}
                  {toggleCamera && (
                    <MenuButton
                      onClick={() => { setMenuOpen(false); toggleCamera(); }}
                      label={cameraOn ? t('players.turnOffCamera') : t('players.turnOnCamera')}
                    />
                  )}
                  {toggleMic && (
                    <MenuButton
                      onClick={() => { setMenuOpen(false); toggleMic(); }}
                      label={micOn ? t('players.muteMic') : t('players.unmuteMic')}
                    />
                  )}

                  {/* ── Device selectors ─────────────────────────────────── */}
                  {(showMicSelector || showCameraSelector) && (
                    <div className="t-border border-t my-1" />
                  )}
                  {showMicSelector && (
                    <div className="px-3 py-2 space-y-1">
                      <p className="text-xs t-text-disabled">{t('players.selectMic')}</p>
                      <select
                        value={selectedMicId ?? ''}
                        onChange={(e) => changeMicDevice(e.target.value || null)}
                        className="w-full text-xs t-bg-input t-text t-border border rounded px-1.5 py-1 t-focus-ring"
                        aria-label={t('players.selectMic')}
                      >
                        {availableMics.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || t('players.deviceUnknown', { n: availableMics.indexOf(d) + 1 })}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {showCameraSelector && (
                    <div className="px-3 py-2 space-y-1">
                      <p className="text-xs t-text-disabled">{t('players.selectCamera')}</p>
                      <select
                        value={selectedCameraId ?? ''}
                        onChange={(e) => changeCameraDevice(e.target.value || null)}
                        className="w-full text-xs t-bg-input t-text t-border border rounded px-1.5 py-1 t-focus-ring"
                        aria-label={t('players.selectCamera')}
                      >
                        {availableCameras.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || t('players.deviceUnknown', { n: availableCameras.indexOf(d) + 1 })}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* ── Audio processing ──────────────────────────────────── */}
                  <div className="t-border border-t my-1" />
                  <div className="px-3 py-2 space-y-2">
                    <p className="text-xs t-text-disabled">{t('players.audioProcessing')}</p>
                    {krispSupported && (
                      <AudioCheckbox
                        checked={krispEnabled ?? true}
                        onChange={toggleKrisp}
                        label={t('players.krispNoise')}
                      />
                    )}
                    <AudioCheckbox
                      checked={audioConstraints?.echoCancellation ?? true}
                      onChange={() => changeAudioConstraints?.({ echoCancellation: !audioConstraints?.echoCancellation })}
                      label={t('players.echoCancellation')}
                    />
                    <AudioCheckbox
                      checked={audioConstraints?.noiseSuppression ?? true}
                      onChange={() => changeAudioConstraints?.({ noiseSuppression: !audioConstraints?.noiseSuppression })}
                      label={t('players.noiseSuppression')}
                    />
                    <AudioCheckbox
                      checked={audioConstraints?.autoGainControl ?? true}
                      onChange={() => changeAudioConstraints?.({ autoGainControl: !audioConstraints?.autoGainControl })}
                      label={t('players.gainControl')}
                    />
                  </div>
                </>
              ) : (
                <>
                  {!inPersonMode && onMuteToggle && (
                    <MenuButton
                      onClick={() => { setMenuOpen(false); onMuteToggle(); }}
                      label={isMuted ? t('players.unmuteForMe') : t('players.muteForMe')}
                    />
                  )}
                  {!inPersonMode && onVolumeChange && (
                    <div className="px-3 py-2 space-y-1">
                      <p className="text-xs t-text-disabled">{t('players.volume')}</p>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={volume}
                        onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                        aria-label={t('players.volumeFor', { name })}
                        className="w-full accent-(--c-action-bg)"
                      />
                    </div>
                  )}
                  {!inPersonMode && onHideToggle && (
                    <MenuButton
                      onClick={() => { setMenuOpen(false); onHideToggle(); }}
                      label={isHidden ? t('players.showCamera') : t('players.hideCamera')}
                    />
                  )}
                  {onKick && (
                    <>
                      {!inPersonMode && <div className="t-border border-t my-1" />}
                      <MenuButton
                        onClick={() => { setMenuOpen(false); onKick(); }}
                        label={t('players.kick')}
                        danger
                      />
                    </>
                  )}
                </>
              )}
            </div>,
            document.body
          )}
        </div>
      )}
    </div>
  );
}

// ── Camera / Mic SVG icons ────────────────────────────────────────────────────
function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
      <path d="M15 8a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2.5l4 2.5V9.5l-4 2.5V8z" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zm-1 3a1 1 0 0 1 2 0v8a1 1 0 0 1-2 0V4z" />
      <path d="M7 11a1 1 0 0 0-2 0 7 7 0 0 0 6 6.92V20H9a1 1 0 0 0 0 2h6a1 1 0 0 0 0-2h-2v-2.08A7 7 0 0 0 19 11a1 1 0 0 0-2 0 5 5 0 0 1-10 0z" />
    </svg>
  );
}

/**
 * Toggle button showing a camera or mic icon.
 * When `on` is false, renders a diagonal strikethrough line in the error colour.
 */
function MediaToggleButton({ on, onClick, icon, 'aria-label': ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={on}
      className="relative w-6 h-6 flex items-center justify-center rounded bg-(--c-bg-page)/65 hover:bg-(--c-bg-page)/85 t-text transition-colors t-focus-ring"
    >
      {icon}
      {!on && (
        <svg
          viewBox="0 0 24 24"
          className="absolute inset-0 w-full h-full pointer-events-none"
          aria-hidden="true"
        >
          <line
            x1="3" y1="21" x2="21" y2="3"
            stroke="var(--c-error)"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function MenuButton({ onClick, label, danger = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full text-left px-3 py-2 text-sm hover:opacity-80 t-focus-ring ${
        danger ? 't-text-error' : 't-text-secondary'
      }`}
    >
      {label}
    </button>
  );
}

function AudioCheckbox({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <ThemedCheckbox
        checked={checked}
        onChange={onChange}
        className="t-checkbox t-focus-ring shrink-0"
      />
      <span className="text-sm t-text-secondary">{label}</span>
    </label>
  );
}
